import type {
  GenerationImpactLayoutWorkspaceState,
  GenerationImpactScenarioInput,
  GenerationImpactScenarioSnapshot,
} from '../../../types/generationImpact';
import type { SiteLayer } from '../../../types/site';
import type { SiteBoundaryParcel } from '../../../types/siteBoundary';
import {
  buildGenerationScenarioLayerGeometry,
  scenarioNoiseContours,
} from '../../../lib/generationImpact/scenarioLayerGeometry';

/**
 * The persisted scenario hash is produced from JSON data. A sorted-key
 * representation gives the workbench a stable dirty check without coupling the
 * browser to Node's SHA-256 implementation or treating harmless key ordering as
 * an edit.
 */
function canonicalValue(value: unknown): unknown {
  if (Array.isArray(value)) return value.map(canonicalValue);
  if (value && typeof value === 'object') {
    return Object.fromEntries(
      Object.entries(value as Record<string, unknown>)
        .filter(([, item]) => item !== undefined)
        .sort(([left], [right]) => left.localeCompare(right))
        .map(([key, item]) => [key, canonicalValue(item)]),
    );
  }
  return value;
}

export function generationScenarioInputMatches(
  input: GenerationImpactScenarioInput,
  snapshot: GenerationImpactScenarioSnapshot | null | undefined,
): boolean {
  if (!snapshot) return false;
  return JSON.stringify(canonicalValue(input)) === JSON.stringify(canonicalValue(snapshot.input));
}

export interface LayoutHeadView {
  version: number;
  publicationId: string | null;
  scenarioId: string;
  scenarioName: string | null;
  scenarioUpdatedAt: string | null;
  inputHash: string | null;
  resultHash: string | null;
  publishedAt: string | null;
  publishedBy: string | null;
  layerIds: string[];
}

export interface LayoutWorkspaceView {
  active: LayoutHeadView | null;
  activeLayers: SiteLayer[];
  legacyUnversioned: boolean;
}

function stringField(record: Record<string, unknown>, ...keys: string[]): string | null {
  for (const key of keys) {
    const value = record[key];
    if (typeof value === 'string' && value.trim()) return value;
  }
  return null;
}

function numberField(record: Record<string, unknown>, ...keys: string[]): number | null {
  for (const key of keys) {
    const value = record[key];
    if (typeof value === 'number' && Number.isFinite(value)) return value;
  }
  return null;
}

/**
 * Keep UI rendering tolerant of additive publication metadata. The named
 * workspace contract remains the boundary, while this adapter makes older
 * emulator data (which may omit recently-added display fields) safe to show.
 */
export function readLayoutWorkspace(
  workspace: GenerationImpactLayoutWorkspaceState | null | undefined,
): LayoutWorkspaceView {
  if (!workspace) return { active: null, activeLayers: [], legacyUnversioned: false };
  const root = workspace as unknown as Record<string, unknown>;
  const rawActive = root.active;
  const activeRecord = rawActive && typeof rawActive === 'object'
    ? rawActive as Record<string, unknown>
    : null;
  const activeLayers = Array.isArray(root.activeLayers) ? root.activeLayers as SiteLayer[] : [];
  const version = activeRecord ? numberField(activeRecord, 'version', 'layoutVersion') : null;
  const scenarioId = activeRecord ? stringField(activeRecord, 'scenarioId') : null;

  return {
    active: activeRecord && version != null && scenarioId
      ? {
          version,
          publicationId: stringField(activeRecord, 'publicationId', 'id'),
          scenarioId,
          scenarioName: stringField(activeRecord, 'scenarioName', 'name'),
          scenarioUpdatedAt: stringField(activeRecord, 'scenarioUpdatedAt'),
          inputHash: stringField(activeRecord, 'inputHash'),
          resultHash: stringField(activeRecord, 'resultHash'),
          publishedAt: stringField(activeRecord, 'publishedAt', 'createdAt'),
          publishedBy: stringField(activeRecord, 'publishedBy', 'createdBy'),
          layerIds: Array.isArray(activeRecord.layerIds)
            ? activeRecord.layerIds.filter((id): id is string => typeof id === 'string')
            : activeLayers.map((layer) => layer.id),
        }
      : null,
    activeLayers,
    legacyUnversioned: root.legacyUnversioned === true,
  };
}

export interface LayoutDraftItem {
  key: string;
  label: string;
}

function safeKey(value: string): string {
  return value.trim().toLowerCase().replace(/[^a-z0-9_-]+/g, '-');
}

/** Logical map contents shown in the review step; publication remains server-owned. */
export function proposedLayoutItems(
  scenario: GenerationImpactScenarioSnapshot,
  parcels: readonly SiteBoundaryParcel[] = [],
): LayoutDraftItem[] {
  if (parcels.length > 0) {
    // The same parcel-backed converter publishes custom components, exclusion
    // rings, receptors, noise bands and barriers. Preserve the existing server
    // richScenarioLayerDrafts role keys so a review identifies replacements
    // rather than inventing two legacy plant/switchyard blocks.
    return buildGenerationScenarioLayerGeometry(scenario, parcels, {
      importedAt: scenario.updatedAt,
    }).layers.map((layer) => {
      const componentKind = layer.attributes?.layoutComponentKind;
      const rolePrefix = componentKind === 'generation-block' || componentKind === 'cooling'
        ? 'plant'
        : componentKind === 'switchyard'
          ? 'switchyard'
          : componentKind === 'ccs-capture' || componentKind === 'ccs-compression'
            ? 'ccs'
            : typeof componentKind === 'string'
              ? `auxiliary-${componentKind}`
              : layer.category === 'impact.receptor'
                ? 'receptor'
                : layer.category === 'impact.noise'
                  ? 'noise'
                  : 'layer';
      const stableSuffix = layer.id.replace(/^genimpact-[^-]+-/, '');
      return { key: `${rolePrefix}:${stableSuffix}`, label: layer.name };
    });
  }
  const items: LayoutDraftItem[] = [
    { key: 'plant', label: `${scenario.name} — generation plant` },
    { key: 'switchyard', label: `${scenario.name} — switchyard` },
  ];
  if (scenario.input.ccs.enabled) items.push({ key: 'ccs', label: `${scenario.name} — CCS and compression` });
  for (const receptor of scenario.input.receptors) {
    if (!receptor.location) continue;
    items.push({ key: `receptor:${receptor.id}`, label: receptor.label });
  }
  for (const contour of scenarioNoiseContours(scenario)) {
    if (contour.status !== 'calculated' || contour.radiusM == null) continue;
    items.push({ key: `noise:${contour.levelDbA}`, label: `${scenario.name} — ${contour.levelDbA} dBA screening contour` });
  }
  return items;
}

function layerLogicalKey(layer: SiteLayer): string | null {
  const attributes = layer.attributes ?? {};
  // `layoutRole` is the canonical backend role. The aliases keep the review
  // readable for emulator publications created while the contract was landing.
  const explicit = attributes.layoutRole ?? attributes.generationImpactLogicalKey ?? attributes.logicalKey ?? attributes.layoutLogicalKey ?? attributes.layerKey;
  if (typeof explicit === 'string' && explicit.trim()) return explicit;
  const suffixes = ['plant', 'switchyard', 'ccs', 'noise-55', 'noise-45'];
  const known = suffixes.find((suffix) => layer.id.endsWith(`-${suffix}`));
  if (known) return known.startsWith('noise-') ? `noise:${known.slice(6)}` : known;
  const receptorIndex = layer.id.lastIndexOf('-receptor-');
  return receptorIndex >= 0 ? `receptor:${safeKey(layer.id.slice(receptorIndex + 10))}` : null;
}

export interface LayoutChangeSummary {
  nextVersion: number;
  added: LayoutDraftItem[];
  updated: LayoutDraftItem[];
  removed: LayoutDraftItem[];
  proposedCount: number;
  currentCount: number;
  samePublishedScenario: boolean;
}

export function summarizeLayoutChanges(
  scenario: GenerationImpactScenarioSnapshot,
  workspace: GenerationImpactLayoutWorkspaceState | null | undefined,
  parcels: readonly SiteBoundaryParcel[] = [],
): LayoutChangeSummary {
  const view = readLayoutWorkspace(workspace);
  const proposed = proposedLayoutItems(scenario, parcels);
  const currentByKey = new Map<string, LayoutDraftItem>();
  for (const layer of view.activeLayers) {
    const key = layerLogicalKey(layer);
    if (key) currentByKey.set(key, { key, label: layer.name });
  }
  const proposedByKey = new Map(proposed.map((item) => [item.key, item]));
  const samePublishedScenario = Boolean(
    view.active
      && view.active.scenarioId === scenario.id
      && view.active.inputHash === scenario.inputHash
      && view.active.resultHash === scenario.resultHash,
  );
  const added = proposed.filter((item) => !currentByKey.has(item.key));
  const updated = proposed.filter((item) => currentByKey.has(item.key) && !samePublishedScenario);
  const removed = [...currentByKey.entries()]
    .filter(([key]) => !proposedByKey.has(key))
    .map(([, item]) => item);

  return {
    nextVersion: (view.active?.version ?? 0) + 1,
    added,
    updated,
    removed,
    proposedCount: proposed.length,
    currentCount: view.activeLayers.length,
    samePublishedScenario,
  };
}

export function shortIntegrityHash(value: string | null | undefined): string {
  return value ? value.slice(0, 8) : 'unknown';
}

export function formatPublicationTime(value: string | null | undefined): string {
  if (!value) return 'time unavailable';
  const date = new Date(value);
  if (!Number.isFinite(date.getTime())) return value;
  return new Intl.DateTimeFormat(undefined, {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(date);
}

export function newLayoutPublishRequestId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') return crypto.randomUUID();
  return `layout-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}
